import { useState, useEffect, useCallback } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import {
  ShoppingBag, ExternalLink, CheckCircle2, Clock, XCircle, Plus,
  FileText, Link2, Globe, ChevronDown, ChevronUp, Loader2, RefreshCw,
  Sparkles, DollarSign, Eye, ArrowRight
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Collapsible, CollapsibleContent, CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusConfig = {
  pending: { label: "Oczekuje", icon: Clock, color: "text-amber-500", bg: "bg-amber-100 text-amber-700" },
  in_progress: { label: "W realizacji", icon: Loader2, color: "text-blue-500", bg: "bg-blue-100 text-blue-700" },
  completed: { label: "Zrealizowane", icon: CheckCircle2, color: "text-emerald-500", bg: "bg-emerald-100 text-emerald-700" },
  published: { label: "Opublikowane", icon: CheckCircle2, color: "text-emerald-500", bg: "bg-emerald-100 text-emerald-700" },
  rejected: { label: "Odrzucone", icon: XCircle, color: "text-red-500", bg: "bg-red-100 text-red-700" },
};

export default function AdvertiserOrders() {
  const { t } = useLanguage();
  const auth = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedOrder, setExpandedOrder] = useState(null);
  
  // Dialog dokupowania artykulow
  const [addSupportDialog, setAddSupportDialog] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [availableSites, setAvailableSites] = useState([]);
  const [selectedSites, setSelectedSites] = useState([]);
  const [ordering, setOrdering] = useState(false);

  const fetchOrders = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/shop/my-orders`);
      setOrders(res.data || []);
    } catch (err) { 
      console.error(err); 
      toast.error("Blad ladowania zamowien");
    }
    finally { setLoading(false); }
  }, []);

  const fetchAvailableSites = async () => {
    try {
      const res = await axios.get(`${API}/shop/search`);
      // Odpowiedź to tablica stron
      const sites = Array.isArray(res.data) ? res.data : [];
      setAvailableSites(sites.map(s => ({
        id: s.site_id,
        name: s.site_name,
        url: s.site_url,
        link_price: s.link_price || s.lowest_price || 50,
        category: s.site_category
      })));
    } catch (err) { console.error(err); }
  };

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  const openAddSupportDialog = (order) => {
    setSelectedOrder(order);
    setSelectedSites([]);
    setAddSupportDialog(true);
    fetchAvailableSites();
  };

  const toggleSiteSelection = (siteId) => {
    setSelectedSites(prev => 
      prev.includes(siteId) 
        ? prev.filter(id => id !== siteId) 
        : [...prev, siteId]
    );
  };

  const handleOrderSupport = async () => {
    if (!selectedOrder || selectedSites.length === 0) {
      toast.error("Wybierz przynajmniej jedna strone");
      return;
    }
    
    setOrdering(true);
    try {
      const res = await axios.post(`${API}/shop/order/${selectedOrder.id}/add-support`, {
        support_sites: selectedSites
      });
      
      if (res.data?.success) {
        toast.success(`Zamowiono ${res.data.articles_ordered} artykulow wspierajacych. Koszt: ${res.data.total_cost} PLN`);
        setAddSupportDialog(false);
        fetchOrders();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Blad zamawiania");
    }
    finally { setOrdering(false); }
  };

  const getStatusBadge = (status) => {
    const cfg = statusConfig[status] || statusConfig.pending;
    return <Badge className={`text-[10px] border-0 ${cfg.bg}`}>{cfg.label}</Badge>;
  };

  // Oblicz calkowity koszt wybranych stron
  const calculateTotalCost = () => {
    return selectedSites.reduce((sum, siteId) => {
      const site = availableSites.find(s => s.id === siteId);
      // Domyslna cena 50 PLN za artykul wspierajacy
      return sum + (site?.link_price || 50);
    }, 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div data-testid="advertiser-orders-page" className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-foreground">Moje zamowienia</h1>
          <p className="text-sm text-muted-foreground mt-1">Historia i status realizacji Twoich zamowien</p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchOrders} className="gap-1">
          <RefreshCw className="w-3 h-3" /> Odswiez
        </Button>
      </div>

      {/* Statystyki */}
      <div className="grid grid-cols-4 gap-4">
        <Card className="rounded-xl">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-primary">{orders.length}</p>
            <p className="text-xs text-muted-foreground">Wszystkie</p>
          </CardContent>
        </Card>
        <Card className="rounded-xl">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-amber-500">{orders.filter(o => o.status === "pending" || o.status === "in_progress").length}</p>
            <p className="text-xs text-muted-foreground">W realizacji</p>
          </CardContent>
        </Card>
        <Card className="rounded-xl">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-emerald-500">{orders.filter(o => o.status === "published" || o.status === "completed").length}</p>
            <p className="text-xs text-muted-foreground">Opublikowane</p>
          </CardContent>
        </Card>
        <Card className="rounded-xl">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-blue-500">{orders.reduce((sum, o) => sum + (o.support_articles_count || 0), 0)}</p>
            <p className="text-xs text-muted-foreground">Art. wspierajace</p>
          </CardContent>
        </Card>
      </div>

      {/* Lista zamowien */}
      {orders.length === 0 ? (
        <Card className="rounded-xl">
          <CardContent className="flex flex-col items-center justify-center py-16 text-muted-foreground">
            <ShoppingBag className="w-12 h-12 mb-4 opacity-20" />
            <p className="text-sm font-medium">Nie masz jeszcze zamowien</p>
            <p className="text-xs">Przejdz do sklepu i zamow publikacje</p>
            <Button variant="default" size="sm" className="mt-4 gap-1" onClick={() => window.location.href = "/shop"}>
              <ShoppingBag className="w-3 h-3" /> Przejdz do sklepu
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {orders.map((order) => (
            <Card key={order.id} className="rounded-xl overflow-hidden">
              <Collapsible open={expandedOrder === order.id} onOpenChange={() => setExpandedOrder(expandedOrder === order.id ? null : order.id)}>
                <CollapsibleTrigger asChild>
                  <CardHeader className="cursor-pointer hover:bg-accent/30 transition-colors pb-3">
                    <div className="flex items-start justify-between">
                      <div className="space-y-1.5">
                        <div className="flex items-center gap-2">
                          <CardTitle className="text-base font-heading">{order.title || order.offer_name || "Zamowienie"}</CardTitle>
                          {getStatusBadge(order.status)}
                        </div>
                        <div className="flex items-center gap-3 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Globe className="w-3 h-3" /> {order.site_name || "—"}
                          </span>
                          <span>{new Date(order.created_at).toLocaleDateString()}</span>
                          <span className="font-medium text-primary">{order.price_zorki || order.total_price || 0} PLN</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {order.support_articles_count > 0 && (
                          <Badge variant="secondary" className="text-[10px]">
                            {order.support_articles_published}/{order.support_articles_count} wspierajacych
                          </Badge>
                        )}
                        {expandedOrder === order.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </div>
                    </div>
                  </CardHeader>
                </CollapsibleTrigger>
                
                <CollapsibleContent>
                  <CardContent className="pt-0 space-y-4">
                    <Separator />
                    
                    {/* Artykul glowny */}
                    <div className="space-y-2">
                      <h4 className="text-xs font-semibold uppercase text-muted-foreground tracking-wider">Artykul glowny</h4>
                      <div className="p-3 rounded-lg bg-muted/50 space-y-2">
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="font-medium text-sm">{order.title || "Artykul w przygotowaniu"}</p>
                            <p className="text-xs text-muted-foreground">{order.site_name}</p>
                          </div>
                          {getStatusBadge(order.status)}
                        </div>
                        {order.published_url && (
                          <a href={order.published_url} target="_blank" rel="noopener noreferrer" 
                            className="inline-flex items-center gap-1.5 text-xs text-primary hover:underline">
                            <ExternalLink className="w-3 h-3" /> {order.published_url}
                          </a>
                        )}
                        {!order.published_url && order.status !== "published" && (
                          <p className="text-xs text-muted-foreground italic">Link bedzie dostepny po publikacji</p>
                        )}
                      </div>
                    </div>
                    
                    {/* Artykuly wspierajace */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <h4 className="text-xs font-semibold uppercase text-muted-foreground tracking-wider">
                          Artykuly wspierajace ({order.support_articles_count || order.support_count_requested || 0})
                          {order.support_articles_pending > 0 && (
                            <span className="ml-2 text-amber-600">• {order.support_articles_pending} oczekuje</span>
                          )}
                        </h4>
                        <Button variant="outline" size="sm" className="h-7 text-xs gap-1" onClick={() => openAddSupportDialog(order)}>
                          <Plus className="w-3 h-3" /> Dodaj wspierajace
                        </Button>
                      </div>
                      
                      {order.support_articles && order.support_articles.length > 0 ? (
                        <div className="space-y-2">
                          {order.support_articles.map((sa, idx) => (
                            <div key={sa.id || idx} className="p-3 rounded-lg bg-muted/30 border border-border/50">
                              <div className="flex items-start justify-between">
                                <div className="space-y-0.5">
                                  <p className="text-sm font-medium">{sa.title || `Artykul wspierajacy #${sa.support_index || idx + 1}`}</p>
                                  <p className="text-xs text-muted-foreground">{sa.site_name || order.site_name || "—"}</p>
                                </div>
                                {getStatusBadge(sa.status)}
                              </div>
                              {sa.published_url && (
                                <a href={sa.published_url} target="_blank" rel="noopener noreferrer" 
                                  className="inline-flex items-center gap-1.5 text-xs text-primary hover:underline mt-1.5">
                                  <ExternalLink className="w-3 h-3" /> Zobacz artykul
                                </a>
                              )}
                              {!sa.published_url && sa.status !== "published" && (
                                <p className="text-xs text-amber-600 mt-1.5 flex items-center gap-1">
                                  <Clock className="w-3 h-3" /> Oczekuje na publikacje
                                </p>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : order.support_articles_requested || order.support_count_requested > 0 ? (
                        <div className="p-4 rounded-lg bg-amber-50 border border-amber-200 text-center">
                          <Clock className="w-5 h-5 text-amber-500 mx-auto mb-1" />
                          <p className="text-sm font-medium text-amber-700">
                            {order.support_count_requested} artykulow wspierajacych zamowionych
                          </p>
                          <p className="text-xs text-amber-600">Oczekuja na przygotowanie i publikacje</p>
                        </div>
                      ) : (
                        <div className="p-4 rounded-lg bg-muted/30 text-center">
                          <p className="text-xs text-muted-foreground">Brak artykulow wspierajacych</p>
                          <Button variant="link" size="sm" className="text-xs mt-1 h-auto p-0" onClick={() => openAddSupportDialog(order)}>
                            Dodaj artykuly wspierajace dla lepszego SEO
                          </Button>
                        </div>
                      )}
                    </div>
                    
                    {/* Szczegoly zamowienia */}
                    <div className="pt-2 border-t">
                      <div className="flex items-center justify-between text-xs text-muted-foreground">
                        <span>ID: {order.id.substring(0, 8)}...</span>
                        <span>Utworzono: {new Date(order.created_at).toLocaleString()}</span>
                      </div>
                    </div>
                  </CardContent>
                </CollapsibleContent>
              </Collapsible>
            </Card>
          ))}
        </div>
      )}

      {/* Dialog dodawania artykulow wspierajacych */}
      <Dialog open={addSupportDialog} onOpenChange={setAddSupportDialog}>
        <DialogContent className="sm:max-w-none max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="text-lg font-heading">Dodaj artykuly wspierajace</DialogTitle>
          </DialogHeader>
          
          {selectedOrder && (
            <div className="space-y-4">
              <div className="p-3 rounded-lg bg-muted/50">
                <p className="text-xs text-muted-foreground">Do zamowienia:</p>
                <p className="font-medium text-sm">{selectedOrder.title || selectedOrder.offer_name}</p>
                <p className="text-xs text-muted-foreground">{selectedOrder.site_name}</p>
              </div>
              
              <div>
                <p className="text-xs font-medium mb-2">Wybierz portale dla artykulow wspierajacych:</p>
                <ScrollArea className="h-[300px] border rounded-lg p-2">
                  <div className="space-y-2">
                    {availableSites.map((site) => (
                      <div key={site.id} 
                        className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                          selectedSites.includes(site.id) ? "bg-primary/10 border border-primary/30" : "hover:bg-accent/50"
                        }`}
                        onClick={() => toggleSiteSelection(site.id)}>
                        <Checkbox checked={selectedSites.includes(site.id)} />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm">{site.name}</p>
                          <p className="text-xs text-muted-foreground truncate">{site.url}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-sm text-primary">{site.link_price || 50} PLN</p>
                          <p className="text-[10px] text-muted-foreground">{site.category}</p>
                        </div>
                      </div>
                    ))}
                    {availableSites.length === 0 && (
                      <p className="text-center text-sm text-muted-foreground py-8">Ladowanie portali...</p>
                    )}
                  </div>
                </ScrollArea>
              </div>
              
              <div className="flex items-center justify-between p-3 rounded-lg bg-primary/5 border border-primary/20">
                <div>
                  <p className="text-xs text-muted-foreground">Wybrano: {selectedSites.length} portali</p>
                  <p className="font-bold text-lg text-primary">{calculateTotalCost()} PLN</p>
                </div>
                <div className="text-right text-xs text-muted-foreground">
                  <p>Artykuly beda wygenerowane</p>
                  <p>automatycznie przez AI</p>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setAddSupportDialog(false)}>Anuluj</Button>
            <Button onClick={handleOrderSupport} disabled={ordering || selectedSites.length === 0} className="gap-1">
              {ordering ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
              Zamow ({selectedSites.length})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
